import _ from "lodash";
import { DefaultValue, selector, selectorFamily } from "recoil";

import { RIDES_KANBAN_DEFAULT_SORT } from "../../constants";
import {
  getRidesSeatsCount,
  getRidesTotalAmount,
  type IRideTableDto,
} from "../../dto";
import {
  DragTypeEnum,
  OccupancyEnum,
  RidesKanbanSortEnum,
  StarredDisplayModeEnum,
} from "../../enums";
import {
  buildTrackingRouteForRide,
  buildTrackingRouteFromRenderState,
  equalDates,
  filterRidesByUserChanged,
  getRideRouteInfo,
  isLiveMapRenderableRideRoute,
  nowTDateISO,
  sortKanbanRides,
  sortRidesByDate,
  storeRidesKanbanSort,
  unique,
} from "../../helpers";
import type { IRideRoute, RydeUiUnit } from "../../models";
import {
  dropdownAtomFamily,
  dropdownsIsFetchingAtom,
  isShowWasSentApprovalMessageAtom,
  nearbyOtherRidesIdsBatchAtom,
  ridesDraggingDataAtom,
  ridesKanbanSortAF,
  rideStationsCollapsedIdsAF,
  rideTrackingAF,
  rideTrackingRenderAF,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
  showSelectedOnlyRowsAtomFamily,
  starredDisplayModeAtomFamily,
  tableRowsAtomFamily,
  tableSearchAF,
  userEditOnlyDisplayModeAtomFamily,
} from "../atoms";
import { boundedSelector, boundedSelectorFamily } from "../selector-factories";
import { isGlobalLoaderActiveSelector } from "./loaders.selectors";
import { companyIdSelector, currentUserIdSelector } from "./profile.selectors";
import {
  applySearchFilter,
  applyStarredLimit,
  getNearbyFilteredRides,
} from "./rides.selectors.utils";
import { timezoneSelector } from "./system-settings.selectors";
import {
  selectedTableRowsIdsSetSF,
  tableRowsComputedSF,
} from "./table-data.selectors";
import { tableFilteredAllGroupsRowsIdsSF } from "./table-layout.selectors";

export const ridesSelector = boundedSelector<IRideTableDto[]>({
  key: "ridesSelector",
  get: ({ get }) => {
    return get(tableRowsAtomFamily("ride")) as IRideTableDto[];
  },
});

/** Per-row grid single-select read — only the row that gained and the row that lost selection re-render. */
export const isSingleSelectedRideIdSF = selectorFamily<boolean, number>({
  key: "isSingleSelectedRideIdSF",
  get:
    rideId =>
    ({ get }) =>
      get(selectedSingleRowRideIdAtom) === rideId,
});

/** The single-selected ride resolved from the current rows — for the side map only (subscribes to the row array). */
export const selectedSingleRowRideDtoSelector = boundedSelector<
  IRideTableDto | undefined
>({
  key: "selectedSingleRowRideDtoSelector",
  get: ({ get }) => {
    const rideId = get(selectedSingleRowRideIdAtom);

    if (rideId === undefined) {
      return undefined;
    }

    const rows = (get(tableRowsAtomFamily("ride")) ?? []) as IRideTableDto[];

    return rows.find(row => row.id === rideId);
  },
});

export const selectedRidesSelector = boundedSelector<IRideTableDto[]>({
  key: "selectedRidesSelector",
  get: ({ get }) => {
    const rides = get(ridesSelector);
    const selectedRideIds = get(selectedTableRowsIdsAtomFamily("ride"));
    let selectedRides: IRideTableDto[] = [];

    if (selectedRideIds.length) {
      const selectedRideIdsSet = new Set(selectedRideIds);
      selectedRides = rides?.filter(x => selectedRideIdsSet.has(x.id)) || [];
      // note: selectedRideIds order is critical for some uses (ex.map routes)
      selectedRides = _.sortBy(selectedRides, e =>
        selectedRideIds?.indexOf(e.id),
      );
    }

    return selectedRides;
  },
});

export const selectedLiveMapMapRidesSF = boundedSelectorFamily<
  IRideTableDto[],
  RydeUiUnit
>({
  key: "selectedLiveMapMapRidesSF",
  get:
    entity =>
    ({ get }) => {
      const checkboxIds = get(selectedTableRowsIdsAtomFamily(entity));
      const rows = (get(tableRowsAtomFamily(entity)) || []) as IRideTableDto[];
      const rowsById = new Map(rows.map(r => [r.id, r]));

      if (checkboxIds.length) {
        return checkboxIds
          .map(id => rowsById.get(id))
          .filter((r): r is IRideTableDto => Boolean(r));
      }

      return [];
    },
});

/** Live map: planned route polylines for checkbox-selected rides. */
export const liveMapRideRoutesSF = boundedSelectorFamily<
  IRideRoute[],
  RydeUiUnit
>({
  key: "liveMapRideRoutesSF",
  get:
    entity =>
    ({ get }) => {
      const rides = get(selectedLiveMapMapRidesSF(entity));

      return rides
        .map(r => getRideRouteInfo(r))
        .filter(isLiveMapRenderableRideRoute);
    },
});

/** Live map: one tracking polyline for a checkbox-selected ride (isolated Recoil deps). */
export const liveMapRideTrackingRouteSF = boundedSelectorFamily<
  IRideRoute | undefined,
  [entity: RydeUiUnit, rideId: number]
>({
  key: "liveMapRideTrackingRouteSF",
  get:
    ([entity, rideId]) =>
    ({ get }) => {
      const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));

      if (!selectedIds.includes(rideId)) {
        return undefined;
      }

      const rows = (get(tableRowsAtomFamily(entity)) || []) as IRideTableDto[];
      const ride = rows.find(row => row.id === rideId);

      if (!ride) {
        return undefined;
      }

      const renderState = get(rideTrackingRenderAF(rideId));

      if (renderState?.pointCount) {
        return buildTrackingRouteFromRenderState({
          rideDto: ride,
          renderState,
        });
      }

      const trackingDtos = get(rideTrackingAF(rideId));

      if (!trackingDtos?.length) {
        return undefined;
      }

      return buildTrackingRouteForRide({
        rideDto: ride,
        trackingDtos,
      });
    },
});

/** Live map: live tracking polylines for rides selected via checkboxes. */
export const liveMapRideTrackingRoutesSF = boundedSelectorFamily<
  IRideRoute[],
  RydeUiUnit
>({
  key: "liveMapRideTrackingRoutesSF",
  get:
    entity =>
    ({ get }) => {
      const checkboxIds = get(selectedTableRowsIdsAtomFamily(entity));

      return checkboxIds
        .map(id => get(liveMapRideTrackingRouteSF([entity, id])))
        .filter((route): route is IRideRoute => Boolean(route));
    },
});

export const selectedRidesSeatsCountSelector = boundedSelector<number>({
  key: "selectedRidesSeatsCountSelector",
  get: ({ get }) => {
    const rides = get(selectedRidesSelector);
    return getRidesSeatsCount(rides);
  },
});

/** Passengers riding on the checkbox-selected rides, deduped — one passenger may ride several of them. */
export const selectedRidesPassengersIdsSelector = boundedSelector<number[]>({
  key: "selectedRidesPassengersIdsSelector",
  get: ({ get }) => {
    const rides = get(selectedRidesSelector);
    return [
      ...new Set(rides.flatMap(ride => ride.metadata?.passengerIds ?? [])),
    ];
  },
});

export const ridesSeatsCountSF = boundedSelectorFamily<number, RydeUiUnit>({
  key: "ridesSeatsCountSF",
  get:
    entity =>
    ({ get }) => {
      const rides = get(tableRowsAtomFamily(entity));
      return getRidesSeatsCount(rides as IRideTableDto[]);
    },
});

export const displayedRidesSeatsCountSF = boundedSelectorFamily<
  number,
  RydeUiUnit
>({
  key: "displayedRidesSeatsCountSF",
  get:
    entity =>
    ({ get }) => {
      const filteredIds = get(tableFilteredAllGroupsRowsIdsSF(entity));
      const rows = get(tableRowsComputedSF(entity)) as IRideTableDto[];
      const displayedRides = rows?.filter(r => filteredIds.has(r.id)) ?? [];
      return getRidesSeatsCount(displayedRides);
    },
});

export const ridesTotalAmountSelector = boundedSelector<number>({
  key: "ridesTotalAmountSelector",
  get: ({ get }) => {
    const rides = get(ridesSelector);
    return getRidesTotalAmount(rides);
  },
});

export const displayedRidesTotalAmountSelector = boundedSelector<number>({
  key: "displayedRidesTotalAmountSelector",
  get: ({ get }) => {
    const filteredIds = get(tableFilteredAllGroupsRowsIdsSF("ride"));
    const rows = get(tableRowsComputedSF("ride")) as IRideTableDto[];
    const displayedRides = rows?.filter(r => filteredIds.has(r.id)) ?? [];
    return getRidesTotalAmount(displayedRides);
  },
});

export const selectedRidesTotalAmountSelector = boundedSelector<number>({
  key: "selectedRidesTotalAmountSelector",
  get: ({ get }) => {
    const rides = get(selectedRidesSelector);
    return getRidesTotalAmount(rides);
  },
});

export const cardRidesSelector = boundedSelector<IRideTableDto[]>({
  key: "cardRidesSelector",
  get: ({ get }) => {
    const editedOnlyDisplayMode = get(
      userEditOnlyDisplayModeAtomFamily("ride"),
    );
    const searchValue = get(tableSearchAF("ride"));
    const ridesRows = get(ridesSelector);
    const starredDisplayMode = get(starredDisplayModeAtomFamily("ride"));
    const skipFilters =
      starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored;
    const starredOnly =
      starredDisplayMode === StarredDisplayModeEnum.FiltersApplied ||
      starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored;
    const nonStarredOnly =
      starredDisplayMode === StarredDisplayModeEnum.ShowAllWithoutStarred;
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);

    const rides = getNearbyFilteredRides(get, ridesRows);

    const searchedRides = applySearchFilter(rides, searchValue, skipFilters);

    const limitedRides = applyStarredLimit({
      get,
      rides: searchedRides,
      starredOnly,
      nonStarredOnly,
      skipFilters,
    });

    let finalRides = skipFilters
      ? limitedRides
      : filterRidesByUserChanged({
          resultRows: limitedRides,
          editedOnlyDisplayMode,
          currentUserId,
          timezone,
        });

    const shouldShowSelectedRowsOnly = get(
      showSelectedOnlyRowsAtomFamily("ride"),
    );
    if (shouldShowSelectedRowsOnly) {
      const selectedIds = get(selectedTableRowsIdsAtomFamily("ride"));
      const selectedIdsSet = new Set(selectedIds);
      finalRides = finalRides.filter(r => selectedIdsSet.has(r.id));
    }

    return sortRidesByDate(finalRides);
  },
});

export const cardRideIdsSelector = boundedSelector<Set<number>>({
  key: "cardRideIdsSelector",
  get: ({ get }) => {
    const rides = get(cardRidesSelector);
    return new Set(rides.map(r => r.id));
  },
});

/** The Kanban card order for the current company. Setting it also remembers the choice in this browser. */
export const ridesKanbanSortSelector = selector<RidesKanbanSortEnum>({
  key: "ridesKanbanSortSelector",
  get: ({ get }) => {
    const companyId = get(companyIdSelector);

    return companyId
      ? get(ridesKanbanSortAF(companyId))
      : RIDES_KANBAN_DEFAULT_SORT;
  },
  set: ({ get, set }, sort) => {
    const companyId = get(companyIdSelector);

    if (!companyId || sort instanceof DefaultValue) {
      return;
    }

    set(ridesKanbanSortAF(companyId), sort);
    storeRidesKanbanSort({ companyId, sort });
  },
});

export const kanbanRidesToDisplaySelector = boundedSelectorFamily<
  IRideTableDto[],
  OccupancyEnum
>({
  key: "kanbanRidesToDisplaySelector",
  get:
    occupancy =>
    ({ get }) => {
      const sort = get(ridesKanbanSortSelector);
      // Subscribe to the area list only under Area sort, so a list refresh does not re-sort the date orders.
      const areaDropdownItems =
        sort === RidesKanbanSortEnum.AreaAsc
          ? get(dropdownAtomFamily("area"))
          : [];

      return sortKanbanRides({
        rides: get(cardRidesSelector).filter(
          ride => ride.occupancy === occupancy,
        ),
        sort,
        areaDropdownItems,
      });
    },
});
export const kanbanRidesCountWithInvalidOccupancySelector =
  boundedSelector<number>({
    key: "kanbanRidesCountWithInvalidOccupancySelector",
    get: ({ get }) => {
      const rides = get(ridesSelector);
      return (
        rides?.filter(
          x =>
            x.occupancy !== OccupancyEnum.High &&
            x.occupancy !== OccupancyEnum.Medium &&
            x.occupancy !== OccupancyEnum.Low,
        )?.length || 0
      );
    },
  });

export const isCollapsedRideStationSF = selectorFamily<
  boolean,
  [rideId: number, stationId: number]
>({
  key: "isCollapsedRideStationSF",
  get:
    ([rideId, stationId]) =>
    ({ get }) => {
      const collapsedIds = get(rideStationsCollapsedIdsAF(rideId || 0));
      const collapsedIdsSet = new Set(collapsedIds);
      return collapsedIdsSet.has(stationId);
    },
  set:
    ([rideId, stationId]) =>
    ({ set }, isSelected) => {
      if (isSelected) {
        set(rideStationsCollapsedIdsAF(rideId || 0), collapsedIds =>
          unique([...collapsedIds, stationId]),
        );
      } else {
        set(rideStationsCollapsedIdsAF(rideId || 0), collapsedIds =>
          collapsedIds?.filter(selId => selId !== stationId),
        );
      }
    },
});

export const isSelectedLiveMapRideRowIdSF = selectorFamily<
  boolean,
  [entity: RydeUiUnit, id: number]
>({
  key: "isSelectedLiveMapRideRowIdSF",
  get:
    ([entity, id]) =>
    ({ get }) => {
      if (entity !== "rideMap") {
        return false;
      }
      const selectedIdsSet = get(selectedTableRowsIdsSetSF(entity));
      return selectedIdsSet.has(id);
    },
});

export const passengerSelfRemovedCountSelector = boundedSelector<number>({
  key: "passengerSelfRemovedCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const passengerSelfRemoved = rows?.filter(
      r => r.metadata?.hasSelfRemovedPassengers,
    );
    return passengerSelfRemoved?.length || 0;
  },
});

export const userEditedRidesCountSelector = boundedSelector<number>({
  key: "userEditedRidesCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const rowsUserEdited = rows?.filter(r => r.updatedUserId);
    return rowsUserEdited?.length || 0;
  },
});

export const userEditedByMeTodayCountSelector = boundedSelector<number>({
  key: "userEditedByMeTodayCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);
    // Read once, not once per row: all four counters must compare against the same instant.
    const nowIso = nowTDateISO(timezone);
    const rowsUserEdited = rows.filter(
      r =>
        r.updatedUserId &&
        r.updatedUserId === currentUserId &&
        r.updatedAt &&
        equalDates(r.updatedAt, nowIso),
    );
    return rowsUserEdited?.length || 0;
  },
});

export const userEditedByMeNotTodayCountSelector = boundedSelector<number>({
  key: "userEditedByMeNotTodayCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);
    const nowIso = nowTDateISO(timezone);
    const rowsUserEdited = rows.filter(
      r =>
        r.updatedUserId &&
        r.updatedUserId === currentUserId &&
        r.updatedAt &&
        !equalDates(r.updatedAt, nowIso),
    );
    return rowsUserEdited?.length || 0;
  },
});

export const userEditedNotByMeTodayCountSelector = boundedSelector<number>({
  key: "userEditedNotByMeTodayCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);
    const nowIso = nowTDateISO(timezone);
    const rowsUserEdited = rows.filter(
      r =>
        r.updatedUserId &&
        r.updatedUserId !== currentUserId &&
        r.updatedAt &&
        equalDates(r.updatedAt, nowIso),
    );
    return rowsUserEdited?.length || 0;
  },
});

export const userEditedNotByMeNotTodayCountSelector = boundedSelector<number>({
  key: "userEditedNotByMeNotTodayCountSelector",
  get: ({ get }) => {
    const rows = get(ridesSelector);
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);
    const nowIso = nowTDateISO(timezone);
    const rowsUserEdited = rows.filter(
      r =>
        r.updatedUserId &&
        r.updatedUserId !== currentUserId &&
        r.updatedAt &&
        !equalDates(r.updatedAt, nowIso),
    );
    return rowsUserEdited?.length || 0;
  },
});

export const pauseFetchNearbyRidesSelector = selector<boolean>({
  key: "pauseFetchNearbyRidesSelector",
  get: ({ get }) => {
    const dragStatus = get(ridesDraggingDataAtom);
    const dataIsFetching = get(isGlobalLoaderActiveSelector);
    const dropdownsIsFetching = get(dropdownsIsFetchingAtom);
    const isDraggingCondition =
      Boolean(dragStatus) && dragStatus.type !== DragTypeEnum.NOT_DRAGGING;

    return isDraggingCondition || dataIsFetching || dropdownsIsFetching;
  },
});

export const draggingBetweenRidesGridIdSelector = selector<number | undefined>({
  key: "draggingBetweenRidesGridIdSelector",
  get: ({ get }) => {
    const dragStatus = get(ridesDraggingDataAtom);
    return (Boolean(dragStatus) &&
      dragStatus.type === DragTypeEnum.GRID_RIDE) ||
      dragStatus.type === DragTypeEnum.GRID_PASSENGER
      ? dragStatus.payload.rideDto.id
      : undefined;
  },
});

export const draggingKanbanRideIdSelector = selector<number | undefined>({
  key: "draggingKanbanRideIdSelector",
  get: ({ get }) => {
    const dragStatus = get(ridesDraggingDataAtom);
    return Boolean(dragStatus) &&
      (dragStatus.type === DragTypeEnum.KANBAN_RIDE ||
        dragStatus.type === DragTypeEnum.CARD_BOARD_RIDE)
      ? dragStatus.payload.rideDto.id
      : undefined;
  },
});

export const isShowWasSentApprovalMessageSelector = selector<boolean>({
  key: "isShowWasSentApprovalMessageSelector",
  get: ({ get }) => {
    const isShowWasSentApprovalMessage = get(isShowWasSentApprovalMessageAtom);
    return isShowWasSentApprovalMessage;
  },
});

export const nearbyOtherRidesIdsBatchCacheSelector = selector({
  key: "nearbyOtherRidesIdsBatchCacheSelector",
  get: ({ get }) => {
    const nearbyOtherRidesIdsBatch = get(nearbyOtherRidesIdsBatchAtom);
    return new Set(Object.keys(nearbyOtherRidesIdsBatch.data).map(Number));
  },
});
